import { useCallback, useState } from 'react';
import html2canvas from 'html2canvas';

interface UseScreenshotReturn {
  capture: () => Promise<Blob | null>;
  capturing: boolean;
  error: string | null;
}

export function useScreenshot(): UseScreenshotReturn {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback(async (): Promise<Blob | null> => {
    setCapturing(true);
    setError(null);
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        logging: false,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        ignoreElements: (el) => {
          // Ignore the FAB button so it doesn't appear in screenshot
          if (el.getAttribute?.('data-issue-reporter') === 'true') return true;
          // Ignore iframes which cause CORS issues
          if (el.tagName === 'IFRAME') return true;
          return false;
        },
      });

      // Verify canvas has actual content (not blank)
      if (canvas.width === 0 || canvas.height === 0) {
        setError('Screenshot captured but was empty');
        return null;
      }

      return new Promise<Blob | null>((resolve) => {
        let resolved = false;
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            setError('Screenshot capture timed out');
            resolve(null);
          }
        }, 5000);

        canvas.toBlob(
          (blob) => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timeout);
            if (!blob || blob.size < 100) {
              setError('Screenshot was too small — may be blank');
              resolve(null);
            } else {
              resolve(blob);
            }
          },
          'image/png',
          0.92,
        );
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown capture error';
      setError(msg);
      console.error('Screenshot capture failed:', msg);
      return null;
    } finally {
      setCapturing(false);
    }
  }, []);

  return { capture, capturing, error };
}
